<template>
  <b-modal
    ref="downloadConfig"
    centered
    size="lg"
    title="Download VPN config for user"
    hide-footer
    scrollable
    footer-bg-variant=""
    @hidden="
      unselectUser(),
      clearUserServers(),
      clearSelectedConfigServer(),
      clearAdvancedCheck(),
      clearAttempt(),
      hideServerConfig()
    ">

    <!-- server select -->
    <b-form-group
      label="Config for server"
      class="mb-8">
      <multiselect
        v-model="selectedConfigServer"
        :options="userServers"
        :show-labels="false"
        :custom-label="i => { return `${i.country.name}, ${i.location} (${i.ip})` }"
        :class="{ 'multiselect--invalid' : !selectedConfigServer && attemptedToGetConfig }"
        :disabled="userServersEmpty || userVpnServersEmpty"
        placeholder="Please select server" />
    </b-form-group>

    <!-- list of vpn servers -->
    <b-list-group
      v-if="!userVpnServersEmpty"
      class="mb-8">
      <b-list-group-item
        v-for="vpnServer in userVpnServers"
        :key="`user-vpn-server-${vpnServer.code}`"
        :class="{ 'bg-light' : isServerConfigPending(vpnServer.code) }"
        class="py-5">

        <!-- title and controls -->
        <header class="d-flex align-items-center flex-wrap">
          <div class="d-flex align-items-center w-100 w-sm-auto mb-4 mb-sm-0">
            <span class="svg-icon svg-icon-sm mr-2">
              <inline-svg
                :src="`media/svg/icons/VPN/${vpnServer.code}.svg`"
                class="svg-icon m-0" />
            </span>
            <span
              class="font-weight-bold line-height-sm d-inline-block text-truncate"
              style="max-width: 200px;">
              {{ vpnServer.name }}
            </span>
          </div>
          <div class="d-flex align-items-center justify-content-sm-end ml-0 ml-sm-auto">
            <b-button
              v-if="isServerConfigPending(vpnServer.code)"
              v-wave
              variant="outline-primary"
              size="sm"
              class="ml-auto py-1 px-2"
              @click="hideServerConfig()">
              <i class="fas fa-eye-slash fa-sm p-0 mr-1" />
              <span>Hide</span>
            </b-button>
            <b-button
              v-else
              v-wave
              variant="outline-primary"
              size="sm"
              class="ml-auto py-1 px-2"
              :disabled="usersResponses('getUserConfig') && isServerConfigPending(vpnServer.code)"
              @click="validateSelectedServer(vpnServer.code, 'json')">
              <i class="fas fa-code fa-sm p-0 mr-1" />
              <span>Show</span>
            </b-button>
            <b-button
              v-wave
              variant="outline-primary"
              size="sm"
              class="ml-3 py-1 px-2"
              @click="validateSelectedServer(vpnServer.code, 'zip')">
              <i class="fas fa-download fa-sm p-0 mr-1" />
              <span>Download (.zip)</span>
            </b-button>
          </div>
        </header>

        <!-- body with textarea -->
        <b-overlay
          :show="isServerConfigPending(vpnServer.code) && usersResponses('getUserConfig')"
          spinner-type="border"
          variant="light"
          spinner-variant="primary"
          opacity="1"
          rounded>
          <template v-if="isServerConfigPending(vpnServer.code)">
            <template v-if="!userConfigHasError">
              <b-form-group class="w-100 mb-3 mt-3 position-relative">
                <b-form-textarea
                  :id="`copy-user-config-${vpnServer.code}`"
                  name="config-textarea"
                  readonly
                  v-model="userConfig"
                  rows="4"
                  no-resize
                  style="min-height: 150px;"
                  class="mb-0 font-size-sm" />
                <b-button
                  v-wave
                  variant="light"
                  size="sm"
                  class="position-absolute btn-icon"
                  style="bottom: 1rem; right: 1rem;"
                  @click="copyInput(`#copy-user-config-${vpnServer.code}`)">
                  <i class="fas fa-copy fa-sm p-0 m-0" />
                </b-button>
              </b-form-group>
              <b-form-group class="mb-0">
                <b-form-checkbox
                  switch
                  v-model="useAdvancedConfig"
                  @change="validateSelectedServer(vpnServer.code, 'json')">
                  Advanced config
                </b-form-checkbox>
              </b-form-group>
            </template>
            <no-data-found
              v-else
              :text="userConfigError.message"
              class="" />
          </template>
        </b-overlay>
      </b-list-group-item>
    </b-list-group>

    <!-- empty state -->
    <no-data-found
      v-else
      text="This user doesn't have any VPN servers"
      class="border rounded mb-8" />

    <!-- user details -->
    <div class="font-weight-bold mb-2">User Details</div>
    <b-list-group
      v-if="selectedUserId !== null"
      class="mb-0">
      <b-list-group-item class="d-flex align-items-center">
        User ID
        <b-badge
          variant="light"
          class="ml-auto font-size-xs">{{ selectedUser.code }}</b-badge>
      </b-list-group-item>
      <b-list-group-item class="d-flex align-items-center">
        Reseller ID
        <span class="ml-auto">{{ selectedUser.item_id }}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex align-items-center">
        Devices
        <span class="ml-auto">{{ selectedUser.devices }}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex align-items-center">
        Created
        <span class="ml-auto">{{ selectedUser.created | moment("DD.MM.YYYY, HH:mm") }}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex align-items-center">
        Expiry
        <span class="ml-auto">{{ selectedUser.expiry | moment("DD.MM.YYYY, HH:mm") }}</span>
      </b-list-group-item>
    </b-list-group>
  </b-modal>
</template>

<script>
import {
  // actions
  GET_USER_CONFIG,
  DOWNLOAD_USER_CONFIG,

  // mutations
  UNSELECT_USER,
  CLEAR_USER_SERVERS,
  CLEAR_USER_CONFIG,
  CLEAR_PENDING_SERVER_CONFIG,
  CLEAR_GET_USER_CONFIG_ERROR
} from '@/core/services/store/users.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showModal,
  hideModal,
  copyInput,
  showToast
} from '@/core/utils'
import moment from 'moment'

export default {
  name: 'ModalDownloadConfig',
  data() {
    return {
      selectedConfigServer: null,
      useAdvancedConfig: false,
      attemptedToGetConfig: false,

      showModal,
      hideModal,
      copyInput,
      showToast,
      moment,
    }
  },
  computed: {
    ...mapGetters([
      'selectedUser',
      'selectedUserId',
      'userServers',
      'userServersEmpty',
      'userVpnServers',
      'userVpnServersEmpty',
      'userConfig',
      'userConfigEmpty',
      'pendingServerConfig',
      'isServerConfigPending',
      'userConfigError',
      'userConfigHasError',
      'usersResponses',
    ]),
  },
  watch: {
    selectedConfigServer() {
      this.clearAdvancedCheck()
      this.hideServerConfig()
    }
  },
  created() {
    eventBus.$on('applySelectedUserServer', server => {
      this.selectedConfigServer = server
    })
  },
  mounted() {
    eventBus.$on('openModal', type => { this.showModal(type) })
  },
  destroyed() {
    eventBus.$off('openModal')
  },
  methods: {
    validateSelectedServer(vpn_code, out) {
      this.attemptedToGetConfig = true

      if (this.selectedConfigServer) {
        if (out === 'json') {
          this.getUserConfig(vpn_code)
        } else {
          this.downloadUserConfig(vpn_code)
        }
      }
    },

    unselectUser() {
      this.$store.commit(UNSELECT_USER)
    },

    clearUserServers() {
      this.$store.commit(CLEAR_USER_SERVERS)
    },

    clearGetUserConfigError() {
      this.$store.commit(CLEAR_GET_USER_CONFIG_ERROR)
    },

    clearSelectedConfigServer() {
      this.selectedConfigServer = null
    },

    clearAdvancedCheck() {
      this.useAdvancedConfig = false
    },

    clearAttempt() {
      this.attemptedToGetConfig = false
    },

    getUserConfig(vpn_code) {
      this.$store.dispatch(GET_USER_CONFIG, {
        id: this.selectedUserId,
        vpn_code,
        payload: {
          cfg: this.useAdvancedConfig ? 'full' : 'minimal',
          ip: this.selectedConfigServer.ip,
          out: 'json'
        }
      })
    },

    hideServerConfig() {
      this.$store.commit(CLEAR_PENDING_SERVER_CONFIG)
      this.$store.commit(CLEAR_USER_CONFIG)
    },

    downloadUserConfig(vpn_code) {
      this.$store.dispatch(DOWNLOAD_USER_CONFIG, {
        id: this.selectedUserId,
        vpn_code,
        payload: {
          cfg: this.useAdvancedConfig ? 'full' : 'minimal',
          ip: this.selectedConfigServer.ip,
          out: 'zip'
        }
      })
    },
  }
}
</script>
